import React from 'react';
import P from 'prop-types';
import { graphql, compose } from 'react-apollo';
import { Link } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import { string, object } from 'yup';
import sanitizeHtml from 'sanitize-html';
import { allowedHtmlTags } from '@becollective/constants';

import { makeStacker } from 'components/ReactStacker/';
import { ThemeContext } from 'components/App/';
import Button from 'components/Button/';
import Icon from 'components/Icon/';
import GraphQLErrorMessage from 'components/form/GraphQLErrorMessage/';
import ErrorMsg from 'components/form/ErrorMsg/';
import { LOGIN_MUTATION } from 'gql/mutations';
import displayLoading from 'utils/displayLoading';
import ensureLoggedIn from 'utils/ensureLoggedIn';
import transformErrors from 'utils/transformErrors';
import config from 'config';
import logo from 'assets/images/becollective-logo.svg';
import { get } from 'lodash';
import './Login.scss';
import VerifyEmail from '../VerifyEmail';
import MfaVerification from './MfaVerification';
import MfaLockout from './MfaLockout';
import ChangePassword from './ChangePassword';

export const isBackendError = (error, name) => {
  return (
    error.graphQLErrors.filter(({ backendName }) => {
      return backendName === name;
    }).length > 0
  );
};

const schema = object().shape({
  email: string()
    .email()
    .min(1)
    .required('Please enter your email'),
  password: string()
    .min(1)
    .required('Please enter your password'),
});
const defaultLoginPromoText = ({ name }) => {
  return `${name} provides vital links for promoting, facilitating, supporting and connecting volunteers and Volunteering Involving Organisations throughout our region.<br />
  <br />
  Our digital volunteer management system helps connect the people in our community with volunteer opportunities and enables our VIOs to schedule, communicate with and manage their volunteers.<br />
  <br />`;
};

const Login = ({ loginMutation, pushView }) => {
  return (
    <ThemeContext.Consumer>
      {(context) => {
        const {
          loginPromoText,
          registerText,
          registerButtonText,
          hidePoweredByText,
          loginTitle,
          primary,
        } = context;
        const loginText = loginPromoText
          ? loginPromoText
          : defaultLoginPromoText(context);
        const { allowedTags, allowedAttributes } = allowedHtmlTags;
        const loginTextSanitised = sanitizeHtml(loginText || '', {
          allowedTags,
          allowedAttributes,
        });
        return (
          <div className="Login">
            <div className="Login-info">
              <div
                className="Login-text"
                dangerouslySetInnerHTML={{ __html: loginTextSanitised }}
              />
              <div className="Login-cta">
                <div
                  className="Login-cta-label"
                  style={{ color: primary ? primary : 'inherit' }}
                >
                  {registerText ? registerText : 'Not a member?'}
                </div>
                <Link className="ml-2" to="/register">
                  <Button color="primary">
                    {registerButtonText ? registerButtonText : 'Register here'}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="Login-main">
              <h2 className="Login-heading">{loginTitle || 'Login now'}</h2>
              {!hidePoweredByText && (
                <div className="Login-bc">
                  Powered by{' '}
                  <img
                    className="Login-bcLogo"
                    src={logo}
                    alt="Be Collective"
                  />
                </div>
              )}
              <Formik
                validationSchema={schema}
                initialValues={{ email: '', password: '' }}
                onSubmit={async (values, { setErrors, setSubmitting }) => {
                  const { email, password } = values;

                  try {
                    const { data } = await loginMutation({
                      variables: {
                        input: {
                          email,
                          password,
                        },
                      },
                    });
                    if (
                      data.login.mfaChallenge &&
                      data.login.mfaChallenge.session
                    ) {
                      pushView(
                        <MfaVerification
                          email={email}
                          session={data.login.mfaChallenge.session}
                          phoneNumber={data.login.mfaChallenge.phoneNumber}
                        />,
                      );
                    } else {
                      const accessToken = data.login.session.id;
                      localStorage.setItem(
                        'accessToken',
                        JSON.stringify(accessToken),
                      );
                      window.location.href = `${config.WEB_URL}/management-portal`;
                    }
                  } catch (err) {
                    console.log(err)
                    if (isBackendError(err, 'user-not-confirmed')) {
                      pushView(
                        <VerifyEmail email={email} password={password} />,
                      );
                    } else if (isBackendError(err, 'mfa-lockout')) {
                      const e = err.graphQLErrors[0];
                      const { hoursTillUnlock } = get(e, 'data');
                      pushView(
                        <MfaLockout
                          onRequestClose={() => {
                            window.location.href = '/';
                          }}
                          hoursLeft={hoursTillUnlock}
                        />,
                      );
                    }
                    else if(isBackendError(err, 'new-password-required')) {
                      const e = err.graphQLErrors[0];
                      const { session } = get(e, 'data');
                      pushView(
                        <ChangePassword
                          email={email}
                          session={session}
                        />,
                      );
                    }

                    setErrors(transformErrors(err));
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting, errors }) => (
                  <Form>
                    <div className="Login-field">
                      <label htmlFor="email">Email</label>
                      <Field
                        id="email"
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                      />
                    </div>
                    <ErrorMsg name="email" />
                    <div className="Login-field">
                      <label htmlFor="password">Password</label>
                      <Field
                        id="password"
                        type="password"
                        name="password"
                        placeholder="Enter your password"
                      />
                    </div>
                    <ErrorMsg name="password" />

                    <GraphQLErrorMessage errors={errors} />
                    <div className="Login-actions">
                      <Button
                        className="Login-submit"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        Log in now
                      </Button>
                      <Button
                        className="mr-0"
                        color="transparent"
                        underlineText
                        rightAlignText
                        onClick={() => {
                          window.open(`${config.WEB_URL}/forgot-password`);
                        }}
                      >
                        <span>I forgot my password</span>
                        <Icon
                          name="launchExternal"
                          className="ml-2"
                          size={Icon.SIZE.xxs}
                        />
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        );
      }}
    </ThemeContext.Consumer>
  );
};

Login.propTypes = {
  loginMutation: P.func.isRequired,
  pushView: P.func,
};

export default compose(
  graphql(LOGIN_MUTATION, { name: 'loginMutation' }),
  ensureLoggedIn,
  makeStacker,
  displayLoading(),
)(Login);
